import { TrackGroup } from "@opstel/query/models/trackGroup";
import { SidePanel } from "@shared/common/components/SidePanel";
import { useSelectedTimeStore } from "@shared/common/contexts/SelectedTime/useSelectedTimeStore";
import { useZoomStore } from "@shared/common/contexts/zoomLevel/useSbdZoom";
import { SelectionState } from "@shared/common/hooks/selectionState";
import { DateTime, Duration, Interval } from "luxon";
import React, { memo, useCallback, useLayoutEffect, useMemo, useRef, useState } from "react";
import { getTimeLineDateTimes, getTrackGap, getTrackHeight } from "../core/functions";
import {
  LEFT_WIDTH,
  RIGHT_WIDTH,
  TIMELINE_DEFAULT_HOUR_WIDTH,
  TIMELINE_HEADER_HEIGHT,
  TIMELINE_PADDING,
  TRACK_FIRST_MARGIN_TOP,
  TRACK_LAST_MARGIN_BOTTOM,
} from "../core/layoutConstants";
import { OccupationsForTrackWithOrder } from "../core/occupations";
import { CurrentTimeLine } from "./CurrentTimeLine";
import { SBDCenter } from "./Layout/SBDCenter";
import { SBDCenterLeft } from "./Layout/SBDCenterLeft";
import { SBDCenterRight } from "./Layout/SBDCenterRight";
import { SBDTopBar } from "./Layout/SBDTopBar";
import { SBDTrackFilter } from "./SBDFilter/SBDTrackFilter";
import { SBDScrollContainer } from "./SBDScrollContainer";
import { SBDTracks } from "./SBDTracks";
import { TimeLines } from "./TimeLine";
import { TimeLineHeader } from "./TimeLineHeader";

interface SBDMainAreaProps {
  readonly occupationsForTracks: OccupationsForTrackWithOrder[];
  readonly trackGroups: TrackGroup[];
  readonly trackFilters: SelectionState;
}

export function SBDMainArea({
  occupationsForTracks: occupationsForTrack,
  trackGroups,
  trackFilters,
}: SBDMainAreaProps): React.JSX.Element | null {
  const trackTitleContainerRef = useRef<HTMLDivElement>(null);
  const rightContainerRef = useRef<HTMLDivElement>(null);
  const tracks = occupationsForTrack.map((i) => i.track);
  const scenarioInterval = useSelectedTimeStore((state) => state.scenarioInterval);

  if (!scenarioInterval) return null;

  return (
    <SBDCenter>
      <SBDCenterLeft tracks={tracks} ref={trackTitleContainerRef} trackGroups={trackGroups} />
      <SBDCenterRight occupationsForTracks={occupationsForTrack} ref={rightContainerRef} />
      <SBDCenterMiddle
        occupationsForTracks={occupationsForTrack}
        trackTitleContainerRef={trackTitleContainerRef}
        rightContainerRef={rightContainerRef}
        trackFilters={trackFilters}
        scenarioInterval={scenarioInterval}
      />
    </SBDCenter>
  );
}

function getStageWidth(scenarioDuration: Duration, hourWidth: number) {
  return 2 * TIMELINE_PADDING + scenarioDuration.as("hours") * hourWidth;
}

interface SBDCenterMiddleProps {
  readonly occupationsForTracks: OccupationsForTrackWithOrder[];
  readonly trackTitleContainerRef: React.RefObject<HTMLDivElement | null>;
  readonly rightContainerRef: React.RefObject<HTMLDivElement | null>;
  readonly trackFilters: SelectionState;
  readonly scenarioInterval: Interval<true>;
}

function SBDCenterMiddle({
  occupationsForTracks: occupationsForTrack,
  trackTitleContainerRef,
  rightContainerRef,
  trackFilters,
  scenarioInterval,
}: SBDCenterMiddleProps) {
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);

  const timeLineHeaderRef = useRef<HTMLDivElement>(null);

  const [scrollContainerRefVisible, setScrollContainerRefVisible] = useState(false);

  const zoomLevel = useZoomStore((state) => state.zoomLevel);
  const zoomIn = useZoomStore((state) => state.zoomIn);
  const zoomOut = useZoomStore((state) => state.zoomOut);

  const { start: scenarioStart, end: scenarioEnd } = scenarioInterval;
  const [focusKoppelingActive, setFocusKoppelingActive] = useState(true);

  const timeLineDateTimes: DateTime[] = getTimeLineDateTimes(scenarioStart, scenarioEnd);

  const [hourWidth, stageWidth] = useMemo(() => {
    const hourWidth = zoomLevel * TIMELINE_DEFAULT_HOUR_WIDTH;
    const stageWidth = getStageWidth(scenarioEnd.diff(scenarioStart), hourWidth);
    return [hourWidth, stageWidth];
  }, [zoomLevel, scenarioStart, scenarioEnd]);

  const stageHeight = useMemo(
    () =>
      occupationsForTrack
        .map(({ track }) => getTrackHeight(track.maxOrder))
        .reduce((a, b) => a + b, 0) +
      getTrackGap().value * (occupationsForTrack.length - 1) +
      TRACK_FIRST_MARGIN_TOP +
      TRACK_LAST_MARGIN_BOTTOM,
    [occupationsForTrack],
  );

  const scrollContainerRefCallback = useCallback((div: HTMLDivElement) => {
    if (div !== null) {
      setScrollContainerRefVisible(true);
      scrollContainerRef.current = div;
    }
  }, []);

  const handleScroll = useCallback(() => {
    if (scrollContainerRef.current) {
      const { scrollTop, scrollLeft } = scrollContainerRef.current;

      if (timeLineHeaderRef.current) {
        timeLineHeaderRef.current.scrollLeft = scrollLeft;
      }
      if (trackTitleContainerRef.current) {
        trackTitleContainerRef.current.scrollTop = scrollTop;
      }
      if (rightContainerRef.current) {
        rightContainerRef.current.scrollTop = scrollTop;
      }
    }
  }, [trackTitleContainerRef, rightContainerRef]);

  const handleWheel = useCallback(
    (event: WheelEvent) => {
      if (!event.ctrlKey) return;

      event.stopPropagation();
      event.preventDefault();
      if (event.deltaY > 0) {
        zoomOut();
      } else {
        zoomIn();
      }
    },
    [zoomOut, zoomIn],
  );

  useLayoutEffect(() => {
    if (!scrollContainerRefVisible) {
      return;
    }

    const current = scrollContainerRef.current!;

    current.addEventListener("wheel", handleWheel, { passive: false });

    return () => {
      if (current) {
        current.removeEventListener("wheel", handleWheel);
      }
    };
  }, [handleWheel, scrollContainerRefVisible]);

  const handleOnDragStart = useCallback(() => {
    setFocusKoppelingActive(false);
  }, []);

  const handleOnDragEnd = useCallback(() => {
    setFocusKoppelingActive(true);
  }, []);

  const [isSidePanelOpen, setIsSidePanelOpen] = useState(false);

  const openFilterSidePanel = useCallback(() => {
    setIsSidePanelOpen(true);
  }, [setIsSidePanelOpen]);

  const closeFilterSidePanel = useCallback(() => {
    setIsSidePanelOpen(false);
  }, [setIsSidePanelOpen]);

  return (
    <div
      className="order-2 block flex h-full flex-col"
      style={{ width: `calc(100% - ${LEFT_WIDTH}px - ${RIGHT_WIDTH}px)` }}
    >
      <TimeLineHeader
        width={stageWidth}
        height={TIMELINE_HEADER_HEIGHT}
        scenarioStart={scenarioStart}
        scenarioEnd={scenarioEnd}
        zoomLevel={zoomLevel}
        timeLineDateTimes={timeLineDateTimes.filter((i) => i.minute === 0 || i.minute === 30)}
        ref={timeLineHeaderRef}
        onDragStart={handleOnDragStart}
        onDragEnd={handleOnDragEnd}
      />
      <SBDTopBar openFilterSidePanel={openFilterSidePanel} />
      <SBDScrollContainer onScroll={handleScroll} ref={scrollContainerRefCallback}>
        <div className="relative h-full" style={{ width: `${stageWidth}px` }}>
          <TimeLinesMemo
            timeLineDateTimes={timeLineDateTimes}
            scenarioStart={scenarioStart}
            hourWidth={hourWidth}
            stageHeight={stageHeight}
            lineType="line"
          />
          <CurrentTimeLine
            stageHeight={stageHeight}
            hourWidth={hourWidth}
            focusKoppelingActive={focusKoppelingActive}
          />
          <SBDTracks
            stageWidth={stageWidth}
            occupationsForTrack={occupationsForTrack}
            zoomLevel={zoomLevel}
          />
        </div>
      </SBDScrollContainer>
      <SidePanel title="Filter" isOpen={isSidePanelOpen} onClose={closeFilterSidePanel}>
        <SBDTrackFilter
          filters={trackFilters.items}
          toggleFilter={trackFilters.toggleCheck}
          setAllChecked={trackFilters.setAllChecked}
          setAllUnchecked={trackFilters.setAllUnchecked}
        />
      </SidePanel>
    </div>
  );
}

const TimeLinesMemo = memo(TimeLines);
